import { GoogleLogin } from "@react-oauth/google";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Native "Continue with Google" button rendered on our own domain.
 * No redirect to a 3rd-party hosted auth page.
 *
 * Renders Google Identity Services' official button via @react-oauth/google.
 * On success: POSTs the JWT credential to our backend /api/auth/google which
 * verifies signature+audience server-side and issues a first-party session
 * cookie. Then redirects to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Anti-flicker note: during navigation/hydration, @react-oauth/google may
 * briefly render two stacked instances (the placeholder + the live iframe)
 * before settling. We constrain the wrapper to a fixed footprint with
 * overflow-hidden so any second instance is clipped and never shows up as a
 * doubled button on the Landing header.
 */

// Pixel sizes from Google Identity Services official spec
// https://developers.google.com/identity/gsi/web/reference/js-reference
const HEIGHT_BY_SIZE = { small: 32, medium: 36, large: 44 };
const WIDTH_BY_SIZE = { small: 200, medium: 240, large: 280 };

export default function GoogleSignInButton({
    size = "large",
    width,
    theme = "filled_black",
    text = "continue_with",
}) {
    const { setUser } = useAuth();
    const navigate = useNavigate();

    const onSuccess = async (response) => {
        const credential = response?.credential;
        if (!credential) {
            toast.error("Google no devolvió un token. Probá de nuevo.");
            return;
        }
        try {
            // Pre-login cleanup: nuke any residual session cookie from a
            // previous (possibly stale) login so the browser doesn't send it
            // alongside the new /auth/google request. Best-effort — if the
            // user was never logged in this is a 401 we ignore.
            try {
                await api.post("/auth/logout");
            } catch (_) {
                /* expected when no previous session — silent */
            }

            const { data } = await api.post("/auth/google", { credential });
            setUser(data);
            toast.success(`Hola, ${data.name?.split(" ")[0] || ""}.`);
            // If the user came from a "Suscribirme" CTA on the Landing while
            // logged out, deep-link them back to the Onvo Checkout right after
            // login so they don't have to find the button again.
            let pendingCheckout = null;
            try {
                pendingCheckout = sessionStorage.getItem("sinpeflow_pending_checkout");
                if (pendingCheckout) sessionStorage.removeItem("sinpeflow_pending_checkout");
            } catch (err) {
                // sessionStorage can throw in private/incognito mode
                console.warn("[auth] sessionStorage access failed:", err);
            }
            if (pendingCheckout && /^https:\/\/(buy|checkout)\.onvo/.test(pendingCheckout)) {
                window.location.href = pendingCheckout;
                return;
            }
            navigate("/dashboard");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Error iniciando sesión");
        }
    };

    const fixedHeight = HEIGHT_BY_SIZE[size] || HEIGHT_BY_SIZE.large;
    const fixedWidth = width || WIDTH_BY_SIZE[size] || WIDTH_BY_SIZE.large;

    return (
        <div
            data-testid="google-signin-button"
            className="relative inline-block overflow-hidden"
            style={{ height: fixedHeight, width: fixedWidth }}
        >
            <GoogleLogin
                onSuccess={onSuccess}
                onError={() => toast.error("No pudimos iniciar sesión con Google")}
                size={size}
                width={fixedWidth}
                theme={theme}
                text={text}
                shape="pill"
                logo_alignment="left"
                useOneTap={false}
                auto_select={false}
            />
        </div>
    );
}
